// @flow

import * as React from 'react';
import { graphql, useMutation } from 'react-relay';
import {
  Button,
  TextField,
  FormHelperText,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import { Box } from 'react-system';
import { prepareCodeChallenge, registerUser } from '@realadvisor/auth-client';
import { useFormik, Form } from '@realadvisor/form';
import { useCaptcha } from '@realadvisor/hooks';
import { encodeGlobalId } from '@realadvisor/transform/global-id';
import { useTheme } from '../../theme';
import { useLocale } from '../../hooks/locale.js';
import { useUserData } from '../../hooks/user-data.js';
import { useAnalytics } from '../../controls/analytics/index.js';
import { Marked } from '../../controls/marked.js';
import { MuiThemeProvider } from '../../controls/mui-providers';
import { Link } from '../../controls/link';
import { GdprLogo } from '../../icons/gdpr-logo';
import { LoadingButton } from '../../controls/loading-button.js';
import { LoginDivider } from './login-divider.js';
import {
  FacebookButton,
  handleFacebookLogin,
  GoogleButton,
  handleGoogleLogin,
} from './login-social.js';
import type { loginContextualCanLoginByEmailMutation } from './__generated__/loginContextualCanLoginByEmailMutation.graphql.js';
import type { loginContextualRequestLinkMutation } from './__generated__/loginContextualRequestLinkMutation.graphql.js';
import type { loginContextualUpdateLeadMutation } from './__generated__/loginContextualUpdateLeadMutation.graphql.js';
import type {
  loginContextualUpdateUserMutation,
  UpdateUserInput,
} from './__generated__/loginContextualUpdateUserMutation.graphql';

type Props = {|
  leadId?: string,
  continueInNewTab?: boolean,
  onContinue?: () => void,
  returnHref: string,
  data?: $Shape<UpdateUserInput>,
  onCancel?: () => void,
  privacyAgreement?: string | null,
  communicationOptIn?: string | null,
  privacyAgreementDescription?:
    | null
    | ((privacyHref: string, termsHref: string) => string),
|};

// How long to wait for a reCAPTCHA token before registering without one.
const CAPTCHA_TOKEN_TIMEOUT_MS = 8000;

const UserForm = props => {
  const { returnUri, data, continueInNewTab, onContinue } = props;
  const { t, language } = useLocale();
  const { track } = useAnalytics();
  const { text } = useTheme();
  const userData = useUserData();
  const { origin, reCaptchaKeyV2, reCaptchaKey } = userData;
  // Invisible v3 first; falls back to the visible v2 checkbox when the
  // server rejects the token. Same pattern as phone-verification.js.
  // The checkbox only appears after the server rejected the v3 token;
  // resubmit once it is solved instead of making the user guess that they
  // have to press "Continue with email" again. Resubmitting from inside
  // Google's callback would run the closure `useCaptcha` captured when the
  // checkbox was rendered, whose captcha state is still `v3`, so it would
  // mint another v3 token instead of sending the solved one. Flag it and let
  // the next render (which sees the cached v2 token) drive the retry, same
  // as phone-verification.js.
  const [captchaSolved, setCaptchaSolved] = React.useState(false);
  const captcha = useCaptcha({
    reCaptchaKeyV2,
    reCaptchaKeyV3: reCaptchaKey,
    onSolved: () => setCaptchaSolved(true),
  });
  // The fail-open timer below must not outlive the form: this component is
  // rendered in a dialog, and registering after the user closed it would
  // mint an account and a session for someone no longer on the page.
  const mountedRef = React.useRef(true);
  const failOpenRef = React.useRef(null);
  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
      clearTimeout(failOpenRef.current);
    };
  }, []);

  const [canLoginByEmail, checking] =
    useMutation<loginContextualCanLoginByEmailMutation>(
      graphql`
        mutation loginContextualCanLoginByEmailMutation(
          $input: CanLoginByEmailInput!
        ) {
          canLoginByEmail(input: $input) {
            result
          }
        }
      `,
    );
  const [requestAuthLink, requesting] =
    useMutation<loginContextualRequestLinkMutation>(
      graphql`
        mutation loginContextualRequestLinkMutation(
          $input: RequestAuthLinkInput!
        ) {
          requestAuthLink(input: $input) {
            errors {
              email
            }
          }
        }
      `,
    );
  const [updateUser] = useMutation<loginContextualUpdateUserMutation>(
    graphql`
      mutation loginContextualUpdateUserMutation($input: UpdateUserInput!) {
        updateUser(input: $input) {
          __typename
        }
      }
    `,
  );
  const [updateLead] = useMutation<loginContextualUpdateLeadMutation>(
    graphql`
      mutation loginContextualUpdateLeadMutation($lead: LeadInput!) {
        upsertLead(input: { lead: $lead }) {
          __typename
        }
      }
    `,
  );
  const [registering, setRegistering] = React.useState(false);

  const callerRef = React.useRef<'google' | 'facebook' | 'email'>('email');
  const form = useFormik<{|
    view: 'collapsed' | 'expanded',
    successMessage: string | null,
    email: string,
    firstName: string,
    lastName: string,
    agreement: boolean,
    policity: boolean,
  |}>({
    initialValues: {
      view: 'collapsed',
      successMessage: null,
      email: '',
      firstName: data?.firstName ?? '',
      lastName: data?.lastName ?? '',
      agreement: false,
      policity: false,
    },
    validate: values => {
      const errors = {};

      if (callerRef.current === 'email') {
        if (values.email === '') {
          errors.email = t('emailRequired');
        }
        if (values.view === 'expanded') {
          if (values.firstName.trim() === '') {
            errors.firstName = t('firstNameRequired');
          }
          if (values.lastName.trim() === '') {
            errors.lastName = t('lastNameRequired');
          }
        }
      }

      if (!values.policity && props.privacyAgreement != null) {
        errors.policity = 'error';
      }

      return errors;
    },
    onSubmit: async values => {
      if (callerRef.current === 'google') {
        handleGoogleLogin({
          userData,
          redirectUri: returnUri,
          target: continueInNewTab === true ? '_blank' : '_self',
        });
        props.onContinue?.();
        return;
      }
      if (callerRef.current === 'facebook') {
        handleFacebookLogin({
          userData,
          redirectUri: returnUri,
          target: continueInNewTab === true ? '_blank' : '_self',
        });
        props.onContinue?.();
        return;
      }

      if (values.view === 'collapsed') {
        const inputEmail = values.email;

        canLoginByEmail({
          variables: {
            input: {
              email: inputEmail,
            },
          },
          onCompleted: payload => {
            if (payload.canLoginByEmail?.result) {
              const redirect_url = new URL('/login-email', origin);
              redirect_url.searchParams.set('redirect_uri', returnUri);
              prepareCodeChallenge().then(code_challenge => {
                requestAuthLink({
                  variables: {
                    input: {
                      email: values.email,
                      code_challenge,
                      redirect_uri: redirect_url.toString(),
                    },
                  },
                  onCompleted: payload => {
                    const error = payload.requestAuthLink?.errors?.email;
                    if (error == null) {
                      if (props.leadId != null) {
                        // We want to store the email in the lead before sending the auth link so that we can track failed email logins
                        updateLead({
                          variables: {
                            lead: {
                              id: props.leadId,
                              emailAddress: inputEmail,
                            },
                          },
                          onError: (error: any) => {
                            // We don't care about the error, we just want to store the email in the lead
                            console.error(error);
                          },
                        });
                      }

                      form.setValues({
                        successMessage: t('authLinkSentTo', {
                          email: values.email,
                        }),
                      });
                    } else {
                      form.setResponseErrors({ email: error });
                    }
                  },
                });
              });
            } else {
              form.setValues({ view: 'expanded' });
            }
          },
        });
      }

      if (values.view === 'expanded') {
        // Loading state starts on click, not when the captcha token resolves:
        // fetching a v3 token can take seconds on a cold cache and the button
        // must not accept a second submit in the meantime.
        setRegistering(true);
        let registerStarted = false;
        const register = async (recaptchaToken, recaptchaVersion) => {
          if (registerStarted || !mountedRef.current) {
            return;
          }
          registerStarted = true;
          try {
            const response = await registerUser({
              email: values.email,
              recaptchaToken,
              recaptchaVersion,
            });
            if (response.ok) {
              const result = await response.json();
              // convert to relay global id
              const id = encodeGlobalId('User', result.id);
              updateUser({
                variables: {
                  input: {
                    ...data,
                    id,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    language,
                  },
                },
                onCompleted: () => {
                  setRegistering(false);
                  track('RA.Web.SignUp.Success', {
                    userId: id,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    emailAddress: values.email,
                    phoneNumber: null,
                    hasAcceptedTerms: true,
                  });
                  window.dataLayer.push({
                    event: 'sign_up',
                    user_id: id,
                    user_data: {
                      first_name: values.firstName,
                      last_name: values.lastName,
                      email_address: values.email,
                      phone_number: null,
                      has_accepted_terms: true,
                    },
                  });

                  onContinue?.();
                  if (continueInNewTab === true) {
                    window.open(returnUri, '_blank');
                  } else {
                    window.location = returnUri;
                  }
                },
              });
            } else {
              setRegistering(false);
              const { error } = await response
                .json()
                .catch(() => ({ error: null }));
              if (
                typeof error === 'string' &&
                error.toLowerCase().includes('captcha')
              ) {
                if (recaptchaToken == null) {
                  // The request went out without a token because the
                  // reCAPTCHA script never delivered one (blocked or failed
                  // to load). The visible checkbox needs that same script, so
                  // offering it would be an empty box with no way forward.
                  form.setResponseErrors({ email: t('somethingWentWrong') });
                } else {
                  // v3 token rejected — show the visible v2 checkbox and let
                  // the user solve it, then resubmit.
                  captcha.verifyUser();
                }
              } else if (error === 'INVALID_EMAIL') {
                // The auth service never sets a status message; the code is
                // in the JSON body.
                form.setResponseErrors({ email: t('emailInvalid') });
              } else {
                form.setResponseErrors({ email: t('somethingWentWrong') });
              }
            }
          } catch (error) {
            // Not rethrown: nothing awaits this promise, so a rethrow would
            // only be an unhandled rejection with no feedback for the user.
            setRegistering(false);
            console.error(error);
            form.setResponseErrors({ email: t('somethingWentWrong') });
          }
        };
        // Fail open while the server is log-only: if the reCAPTCHA script is
        // blocked (ad blockers, CSP) or `execute` never settles, the token
        // callback is never invoked. Registering without a token keeps the
        // pre-captcha behaviour and the server logs "would reject" for it.
        clearTimeout(failOpenRef.current);
        failOpenRef.current = setTimeout(() => {
          register(null, null);
        }, CAPTCHA_TOKEN_TIMEOUT_MS);
        captcha.requestToken((recaptchaToken, recaptchaVersion) => {
          clearTimeout(failOpenRef.current);
          register(recaptchaToken, recaptchaVersion);
        });
      }
    },
  });

  React.useEffect(() => {
    if (captchaSolved) {
      setCaptchaSolved(false);
      form.submitForm();
    }
    // `form` is recreated every render; only the solved flag should trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [captchaSolved]);

  return (
    <div>
      <div css={{ display: 'grid', gap: 16 }}>
        <GoogleButton
          onClick={() => {
            callerRef.current = 'google';
            form.submitForm();
          }}
        />
        <FacebookButton
          onClick={() => {
            callerRef.current = 'facebook';
            form.submitForm();
          }}
        />
      </div>
      <LoginDivider>{t('or')}</LoginDivider>
      <Form
        css={{ display: 'grid', gap: 16, overflow: 'hidden' }}
        onSubmit={() => {
          callerRef.current = 'email';
          form.submitForm();
        }}
      >
        <TextField
          type="email"
          variant="outlined"
          label={t('email')}
          error={form.errors.email != null}
          helperText={form.errors.email ?? form.values.successMessage}
          value={form.values.email}
          onChange={event =>
            form.setValues({
              email: event.target.value,
              view: 'collapsed',
              successMessage: null,
            })
          }
          onBlur={() => form.setTouched({ email: true })}
        />
        {form.values.view === 'expanded' && (
          <>
            <TextField
              variant="outlined"
              label={t('firstName')}
              error={form.errors.firstName != null}
              helperText={form.errors.firstName}
              value={form.values.firstName}
              onChange={event =>
                form.setValues({ firstName: event.target.value })
              }
              onBlur={() => form.setTouched({ firstName: true })}
            />
            <TextField
              variant="outlined"
              label={t('lastName')}
              error={form.errors.lastName != null}
              helperText={form.errors.lastName}
              value={form.values.lastName}
              onChange={event =>
                form.setValues({ lastName: event.target.value })
              }
              onBlur={() => form.setTouched({ lastName: true })}
            />
          </>
        )}
        {captcha.element !== false && (
          <div css={{ display: 'flex', justifyContent: 'center' }}>
            {captcha.element}
          </div>
        )}
        <LoadingButton
          delay={200}
          loading={checking || requesting || registering}
          color="primary"
          size="medium"
          onClick={() => {
            callerRef.current = 'email';
            form.submitForm();
          }}
        >
          {t('continueWithEmail')}
        </LoadingButton>

        {form.errors.policity != null && (
          <FormHelperText variant="outlined" error={true} filled={true}>
            {t('agreementsError')}
          </FormHelperText>
        )}
        <div css={{ display: 'grid' }}>
          {props.privacyAgreement != null && (
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={form.values.policity}
                  css={
                    form.errors.policity != null ? { color: '#F44336' } : null
                  }
                  onChange={event =>
                    form.setValues({ policity: event.target.checked })
                  }
                />
              }
              label={
                <div
                  css={[
                    text.caption,
                    {
                      p: { margin: 0 },
                    },
                  ]}
                >
                  <Marked>{props.privacyAgreement}</Marked>
                </div>
              }
            />
          )}
          {props.communicationOptIn != null && (
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={form.values.agreement}
                  css={
                    form.errors.agreement != null ? { color: '#F44336' } : null
                  }
                  onChange={event =>
                    form.setValues({ agreement: event.target.checked })
                  }
                />
              }
              label={
                <div
                  css={[
                    text.caption,
                    {
                      p: { margin: 0 },
                    },
                  ]}
                >
                  <Marked>{props.communicationOptIn}</Marked>
                </div>
              }
            />
          )}
        </div>
      </Form>
      {props.privacyAgreementDescription != null && (
        <Link href={{ pathname: '/terms' }}>
          {({ href: termsHref }) => (
            <Link href={{ pathname: '/privacy' }}>
              {({ href: privacyHref }) => (
                <div css={text.caption}>
                  {props.privacyAgreementDescription != null && (
                    <Marked>
                      {props.privacyAgreementDescription(
                        privacyHref,
                        termsHref,
                      )}
                    </Marked>
                  )}
                </div>
              )}
            </Link>
          )}
        </Link>
      )}
      <GdprLogo css={{ width: 180, height: 50 }} />
    </div>
  );
};

export const LoginContextual = (props: Props): React.Node => {
  const { continueInNewTab, onCancel, onContinue } = props;
  const { t } = useLocale();
  const { colors } = useTheme();
  const { origin } = useUserData();
  const returnHref = props.returnHref.startsWith('http')
    ? props.returnHref
    : origin + props.returnHref;

  // MuiThemeProvider is used a LoginContextual can beloaded in no mui interface
  return (
    <MuiThemeProvider>
      <div css={{ width: '100%' }}>
        <UserForm
          leadId={props.leadId}
          returnUri={returnHref}
          data={props.data}
          continueInNewTab={continueInNewTab}
          onContinue={onContinue}
          communicationOptIn={
            props.communicationOptIn === undefined
              ? t('communicationOptIn')
              : props.communicationOptIn
          }
          privacyAgreement={
            props.privacyAgreement === undefined
              ? t('privacyAgreement')
              : props.privacyAgreement
          }
          privacyAgreementDescription={
            props.privacyAgreementDescription === undefined
              ? (privacyHref, termsHref) =>
                  t('privacyAgreementDescriptionDefault', {
                    privacyHref,
                    termsHref,
                  })
              : props.privacyAgreementDescription
          }
        />
        {onCancel && (
          <Box mt={3}>
            <Button
              variant="text"
              css={{ color: colors.disabledText }}
              onClick={onCancel}
            >
              {t('noThankYou')}
            </Button>
          </Box>
        )}
      </div>
    </MuiThemeProvider>
  );
};
